import { getToken } from "../utils/auth";
import { API_BASE_URL } from "./URL_Header";

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
});

const handleResponse = async (response, fallbackMessage) => {
  if (!response.ok) {
    let message = fallbackMessage;
    try {
      const data = await response.json();
      message = data.message || message;
    } catch {
      // ignore parse errors
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return response.json();
};

export const fetchPrintDashboard = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/Dashboard`, {
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to load production dashboard");
};

export const startPrintSession = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/StartSession`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to start print session");
};

export const fetchAvailablePrintOrders = async (
  page = 0,
  pageSize = 20,
  search = "",
  statuses = []
) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (search?.trim()) {
    params.set("search", search.trim());
  }
  if (statuses?.length) {
    params.set("statuses", statuses.join(","));
  }
  const response = await fetch(
    `${API_BASE_URL}/PrintQueue/AvailableOrders?${params.toString()}`,
    { headers: authHeaders() }
  );
  return handleResponse(response, "Failed to load orders");
};

export const fetchPrintPlacementHint = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/PlacementHint`, {
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to load placement hint");
};

export const addOrdersToPrintQueue = async (orderIds, { startNewBatch = false } = {}) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/AddOrders`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ orderIds, startNewBatch }),
  });
  return handleResponse(response, "Failed to add orders to print queue");
};

export const completePrintBatch = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/CompleteBatch`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to complete batch");
};

export const discardEmptyBatch = async (printBatchId) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/EmptyBatch/${printBatchId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to discard empty batch");
};

export const removePrintQueueItem = async (itemId) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/RemoveItem/${itemId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to remove item");
};

export const removeOrderFromPrint = async (orderId) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/RemoveOrder`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ orderId }),
  });
  return handleResponse(response, "Failed to remove order from print");
};

export const removeItemAndUpdateOrderStatus = async (printQueueItemId, status, note = null) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/RemoveAndUpdateStatus`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ printQueueItemId, status, note }),
  });
  return handleResponse(response, "Failed to remove item and update order status");
};

export const movePrintItemToSlot = async (printQueueItemId, machinePosition, printBatchId = null) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/MoveItem`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ printQueueItemId, machinePosition, printBatchId }),
  });
  return handleResponse(response, "Failed to move item");
};

export const placePrintItemInSlot = async ({
  machinePosition,
  designCode,
  orderId,
  itemIndexInOrder,
  totalItemsInOrder,
  printBatchId = null,
}) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/PlaceItem`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      machinePosition,
      designCode,
      orderId,
      itemIndexInOrder,
      totalItemsInOrder,
      printBatchId,
    }),
  });
  return handleResponse(response, "Failed to place item in slot");
};

export const refreshPrintBatch = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/RefreshBatch`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to refresh batch");
};

export const closePrintSession = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/CloseSession`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to close session");
};

export const fetchPrintedItems = async (page = 0, pageSize = 12) => {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await fetch(
    `${API_BASE_URL}/PrintQueue/PrintedItems?${params.toString()}`,
    { headers: authHeaders() }
  );
  return handleResponse(response, "Failed to load printed items");
};

export const reprintPrintItem = async (itemId) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/ReprintItem/${itemId}`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to reprint item");
};

export const resetBatchCounter = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/ResetBatchCounter`, {
    method: "POST",
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to reset batch counter");
};

export const fetchAllPendingLabels = async () => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/AllPendingLabels`, {
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to load pending labels");
};

export const fetchBatchView = async (offset = 0) => {
  const params = new URLSearchParams({ offset: String(offset) });
  const response = await fetch(`${API_BASE_URL}/PrintQueue/BatchView?${params.toString()}`, {
    headers: authHeaders(),
  });
  return handleResponse(response, "Failed to load batch view");
};

export const swapPrintItems = async (itemIdA, itemIdB) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/SwapItems`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ itemIdA, itemIdB }),
  });
  return handleResponse(response, "Failed to swap items");
};

export const replacePrintDesignImage = async (printQueueItemId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(
    `${API_BASE_URL}/PrintQueue/ReplaceDesignImage/${printQueueItemId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
      body: formData,
    }
  );
  return handleResponse(response, "Failed to replace design image");
};

export const reservePrintItemReturn = async (printQueueItemId, returnId = null) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/UseReturn`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ printQueueItemId, returnId }),
  });
  return handleResponse(response, "Failed to use returned piece");
};

export const switchPrintItemToNew = async (printQueueItemId) => {
  const response = await fetch(`${API_BASE_URL}/PrintQueue/UseNew`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ printQueueItemId }),
  });
  return handleResponse(response, "Failed to switch item to new print");
};
